import { Dispatch, SetStateAction, SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Tag, TagEntityType, TempTag } from '../types';
import { REQUEST_STATUS } from '@shared/constants/otherConstants';
import {
    Autocomplete,
    Box,
    ButtonProps,
    Chip,
    CircularProgress,
    createFilterOptions,
    Divider,
    MenuItem,
    SxProps,
    TextField,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import { selectGetTagsStatus, selectTags } from '@features/tags/store/selectors';
import { getTagsRequest, getTagsReset } from '@features/tags/store';
import UtmsChip from '@shared/components/ui/UtmsChip';
import UtmsButton from '@shared/components/ui/UtmsButton';
import { useTagsChunk } from '@features/tags/hooks/useTagsChunk';
import { toTitleCase } from '@shared/utils/utilsOld';
import ComponentWithChunks from '@shared/components/others/ComponentWithChunk/ComponentWithChunk';

export interface TagSelectorData {
    tags: (Tag | TempTag)[];

    [x: string | number | symbol]: unknown;
}

interface TagSelectorProps<T extends TagSelectorData> {
    sx?: SxProps;
    limitTags?: number;
    readonly?: boolean;
    fullWidth?: boolean;
    data: T;
    setData: Dispatch<SetStateAction<T>>;
    entityType: TagEntityType;
    height?: number;
}

const filter = createFilterOptions<Tag | TempTag>();

const TagSelector = <T extends TagSelectorData>(props: TagSelectorProps<T>) => (
    <ComponentWithChunks<TagSelectorProps<T>>
        featuresArray={['tags']}
        hooksArray={[useTagsChunk]}
        Component={TagSelectorComponent}
        {...props}
    />
);

const TagSelectorComponent = <T extends TagSelectorData>({
    sx,
    limitTags,
    readonly,
    fullWidth,
    data,
    setData,
    entityType,
    height,
    ...props
}: TagSelectorProps<T>) => {
    const getTagsStatus = useSelector(selectGetTagsStatus);
    const { t } = useTranslation();
    const tags: Tag[] = useSelector(selectTags);
    const [allTagsOptions, setAllTagsOptions] = useState<(Tag | TempTag)[]>([]);
    const [open, setOpen] = useState(false);
    const dispatch = useDispatch();

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleChangeTagSelector = (_event: SyntheticEvent<Element, Event>, value: (Tag | TempTag)[]) => {
        setData((prevData) => ({
            ...prevData,
            tags: !value.length && !readonly ? prevData.tags.filter((item) => item.auto) : value,
        }));
    };

    useEffect(() => {
        dispatch(getTagsReset());
        // @ts-ignore
        dispatch(getTagsRequest(entityType));
    }, [dispatch, entityType]);

    useEffect(() => {
        if (tags) {
            setAllTagsOptions(readonly ? tags : tags.filter((item) => !item.auto));
        }
    }, [readonly, tags]);

    const fixedOptions = useMemo(() => data.tags && data.tags.filter((item) => item.auto), [data.tags]);

    return (
        <Autocomplete
            sx={{
                '& .MuiAutocomplete-inputRoot': {
                    flexWrap: 'nowrap',
                },
                '& .Mui-focused.MuiAutocomplete-inputRoot': {
                    flexWrap: 'wrap',
                },
                zIndex: 10,
                ...sx,
            }}
            limitTags={limitTags || 4}
            fullWidth={fullWidth}
            options={allTagsOptions}
            open={open}
            onOpen={handleOpen}
            onClose={handleClose}
            slotProps={{
                chip: {
                    sx: {
                        fontSize: '0.813rem',
                    },
                },
            }}
            isOptionEqualToValue={(option, value) => option.text === value.text}
            getOptionLabel={(option) => option.text || ''}
            onChange={handleChangeTagSelector}
            value={data.tags}
            renderTags={(value, getTagProps) =>
                value.map((tag, index) => {
                    const { key, ...tagProps } = getTagProps({ index });

                    return (
                        <UtmsChip
                            variant={tag.auto ? 'filled' : 'outlined'}
                            key={key}
                            label={tag.text}
                            {...tagProps}
                            disabled={
                                !readonly &&
                                Boolean(fixedOptions.find((item) => 'pk' in item && 'pk' in tag && item.pk === tag.pk))
                            }
                        />
                    );
                })
            }
            size="small"
            multiple
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={t('tags')}
                    slotProps={{
                        input: {
                            ...params.InputProps,
                            sx: {
                                minHeight: height || 42,
                            },
                            endAdornment: (
                                <>
                                    {getTagsStatus === REQUEST_STATUS.LOADING && (
                                        <CircularProgress
                                            sx={{ position: 'absolute', right: '40px', flexShrink: 0 }}
                                            size={20}
                                        />
                                    )}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        },
                    }}
                    placeholder={toTitleCase(t('type_tag') + '...')}
                />
            )}
            renderOption={(props, option, state) => {
                const matches = match(option.text, state.inputValue, { insideWords: true });
                const parts = parse(option.text, matches);
                const isTempTag = !('pk' in option);

                return isTempTag && !option.wasAdded ? (
                    <>
                        <Divider />
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                p: '6px 16px',
                                gap: 2,
                            }}
                        >
                            <Typography
                                component="span"
                                sx={{
                                    fontSize: '1rem',
                                    fontWeight: 700,
                                    mb: 0.25,
                                }}
                            >
                                {option.text}
                            </Typography>
                            <UtmsButton
                                {...(props as ButtonProps)}
                                onClick={(e) => {
                                    props?.onClick && props.onClick(e as any);
                                    const newTag = { ...option, wasAdded: true };

                                    setAllTagsOptions((prev) => [...prev, newTag]);
                                    setData((prev) => ({
                                        ...prev,
                                        tags: [...prev.tags.filter((tag) => tag.text !== option.text), newTag],
                                    }));
                                }}
                                size="small"
                                startIcon={<AddIcon />}
                            >
                                {t('new') + ' ' + t('tag')}
                            </UtmsButton>
                        </Box>
                    </>
                ) : (
                    <MenuItem
                        disabled={!readonly && option.auto}
                        {...props}
                        key={'pk' in option ? option.pk : undefined}
                    >
                        {state.selected ? (
                            <Chip size="small" sx={{ fontSize: '0.813rem' }} label={option.text} />
                        ) : (
                            parts.map((part, index) => (
                                <Typography
                                    component="span"
                                    key={index}
                                    sx={{
                                        fontSize: '1rem',
                                        fontWeight: part.highlight ? 700 : 400,
                                    }}
                                >
                                    {part.text}
                                </Typography>
                            ))
                        )}
                    </MenuItem>
                );
            }}
            disableCloseOnSelect
            filterOptions={
                !readonly
                    ? (options, params) => {
                          const filtered: (Tag | TempTag)[] = filter(options, params);
                          const { inputValue } = params;

                          // Suggest the creation of a new value
                          const isExisting = options.some((option) => inputValue === option.text);
                          if (inputValue !== '' && !isExisting) {
                              filtered.push({
                                  text: inputValue,
                                  auto: false,
                                  wasAdded: false,
                              });
                          }

                          return filtered;
                      }
                    : undefined
            }
            {...props}
        />
    );
};

export default TagSelector;
